import Cookies from 'js-cookie';
import { isAndroid, isIOS, isMobile, isTablet } from 'react-device-detect';
import { JWTPayload } from './tokenUtils';
import { isAnalyticsTurnedOn } from '@/constants';
import AppConfig, { IS_DEVELOPMENT_ENV, IS_STAGING_ENV } from '@/AppConfig';
import clevertap from 'clevertap-web-sdk';
import { EDeviceOS, getDeviceOS, getDeviceVersionName } from './deviceDetails';

const IS_WEBVIEW_STORAGE_KEY = 'isWebView';

export type DevicePlatform = 'ios' | 'android' | 'web';
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceContext {
  device_platform: DevicePlatform;
  device_type: DeviceType;
  is_webview: boolean;
  app_version_name: string | null;
}

let cachedDeviceContext: DeviceContext | null = null;

const isWebViewRuntime = (): boolean => {
  return (
    Cookies.get(IS_WEBVIEW_STORAGE_KEY) === 'true' ||
    sessionStorage.getItem(IS_WEBVIEW_STORAGE_KEY) === 'true'
  );
};

const resolveDevicePlatform = (isWebView: boolean): DevicePlatform => {
  const nativeOS = getDeviceOS();
  if (isWebView && nativeOS === EDeviceOS.IOS) return 'ios';
  if (isWebView && nativeOS === EDeviceOS.ANDROID) return 'android';
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'web';
};

const resolveDeviceType = (): DeviceType => {
  if (isTablet) return 'tablet';
  if (isMobile) return 'mobile';
  return 'desktop';
};

export const getDeviceContext = (): DeviceContext => {
  if (cachedDeviceContext) return cachedDeviceContext;

  const isWebView = isWebViewRuntime();

  cachedDeviceContext = {
    device_platform: resolveDevicePlatform(isWebView),
    device_type: resolveDeviceType(),
    is_webview: isWebView,
    app_version_name: getDeviceVersionName(),
  };

  return cachedDeviceContext;
};

export const initTracker = () => {
  // CleverTap initialization
  clevertap.privacy.push({ optOut: false }); // Set the flag to true, if the user of the device opts out of sharing their data
  clevertap.privacy.push({ useIP: false }); // Set the flag to true, if the user agrees to share their IP data
  clevertap.init(AppConfig.CLEVERTAP_ACCOUNT_ID, 'in1');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AppConfig.ANALYTICS_LOADED_EVENT));
  }
};

const track = ({ event = '', data = {} }) => {
  if (IS_DEVELOPMENT_ENV || IS_STAGING_ENV) {
    window.dev.i('SEGMENT: track', event, data);
  }
  if (window?.NativeBridge && window?.NativeBridge.callAnalytics) {
    window?.NativeBridge?.callAnalytics?.(event, JSON.stringify(data));
  } else if (isAnalyticsTurnedOn) {
    try {
      if (!clevertap) {
        console.warn(
          'CleverTap script was not loaded in the browser. Hence tracking event to CleverTap failed'
        );
        return;
      }
      clevertap.event.push(event, data);
    } catch (e) {
      console.error(
        'Error encountered while trying to send tracking event to CleverTap',
        e
      );
    }
  }
};

const identifyUserToAnalytics = (user: Partial<JWTPayload>) => {
  if (isAnalyticsTurnedOn) {
    try {
      if (!clevertap) {
        console.warn(
          'CleverTap script was not loaded in the browser. Hence identifying user failed'
        );
        return;
      }
      clevertap.profile.push({
        Site: {
          Identity: user.memberId!,
          Email: user.emailId,
          LastSeenAt: new Date(),
          OrganisationId: user.organisationId,
        },
      });
    } catch (e) {
      console.error(
        'Error encountered while trying to identify user on CleverTap',
        e
      );
    }
  }
};

const trackPageView = ({
  screenName,
  options,
  category,
}: {
  screenName: string;
  options?: unknown;
  category?: string;
}) => {
  if (window?.NativeBridge && window?.NativeBridge.sendScreen) {
    window?.NativeBridge?.sendScreen?.(
      screenName,
      JSON.stringify(options),
      category
    );
  } else if (isAnalyticsTurnedOn) {
    try {
      if (!clevertap) {
        console.warn(
          'CleverTap script was not loaded in the browser. Hence tracking page view failed'
        );
        return;
      }
      clevertap.event.push('pageview', { screenName, category });
    } catch (e) {
      console.error(
        'Error encountered while trying to send page view event to CleverTap',
        e
      );
    }
  }
};

track.identifyUserToAnalytics = identifyUserToAnalytics;

export { track, trackPageView };
